import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * /terms — PlayFirst Terms of Service.
 *
 * Rewritten Feb 2026 per legal positioning requirements. Long-form, plain
 * language, professional + legally cautious. Wording must not be casually
 * edited without legal review.
 */
export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-serif text-primary-900">Terms of Service</h1>
              <p className="text-sm text-stone-500">Last updated: 25 May 2026</p>
            </div>
          </div>

          <article className="prose prose-stone max-w-none text-stone-700 leading-relaxed space-y-6">

            <h2 className="text-xl font-serif text-primary-900 mt-8">1. Acceptance of Terms</h2>
            <p>By accessing or using PlayFirst, you agree to these Terms of Service.</p>
            <p>If you do not agree, you must not use the platform.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">2. Intended Users</h2>
            <p>PlayFirst is intended for:</p>
            <ul className="list-disc pl-6">
              <li>speech-language therapists;</li>
              <li>speech-language pathologists;</li>
              <li>allied health professionals;</li>
              <li>authorised therapy professionals;</li>
              <li>clinics and practices;</li>
              <li>supervised students where permitted.</li>
            </ul>
            <p>PlayFirst is not intended for direct use by children.</p>
            <p>Parent/caregiver access, where available, is controlled by the therapist through invite-based functionality.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">3. Regional Product Positioning</h2>
            <p>PlayFirst Full is intended for users in:</p>
            <ul className="list-disc pl-6">
              <li>South Africa;</li>
              <li>Australia;</li>
              <li>New Zealand;</li>
              <li>the United States;</li>
              <li>the United Kingdom;</li>
              <li>Ireland.</li>
            </ul>
            <p>PlayFirst Lite is intended for de-identified use only.</p>
            <p>
              PlayFirst is designed with HIPAA-aware privacy and security principles in mind. PlayFirst is not currently
              marketed as HIPAA compliant and does not currently operate under a Business Associate Agreement (BAA)
              chain.
            </p>
            <p>Users remain responsible for ensuring their use complies with applicable professional and legal obligations.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">4. Description of the Service</h2>
            <p>PlayFirst provides:</p>
            <ul className="list-disc pl-6">
              <li>therapy planning tools;</li>
              <li>documentation tools;</li>
              <li>AI-assisted drafting tools;</li>
              <li>report generation tools;</li>
              <li>activity generation tools;</li>
              <li>parent collaboration features;</li>
              <li>workflow support.</li>
            </ul>
            <p>PlayFirst Full stores therapist-entered client-related information.</p>
            <p>
              PlayFirst Lite is a separate stateless tool designed for de-identified use only and does not intentionally
              store clinical inputs or AI outputs in a PlayFirst-controlled database.
            </p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">5. No Medical or Clinical Advice</h2>
            <p>PlayFirst does not independently provide:</p>
            <ul className="list-disc pl-6">
              <li>medical advice;</li>
              <li>diagnostic advice;</li>
              <li>clinical decision-making advice.</li>
            </ul>
            <p>The Service provides drafting, planning and workflow support only.</p>
            <p>AI-generated outputs:</p>
            <ul className="list-disc pl-6">
              <li>are drafts;</li>
              <li>require professional review;</li>
              <li>may contain inaccuracies;</li>
              <li>should not replace professional judgment.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">6. User Responsibilities</h2>
            <p>Users agree to:</p>
            <ul className="list-disc pl-6">
              <li>enter only the minimum necessary information;</li>
              <li>use minimal identifiers where possible;</li>
              <li>obtain appropriate consent or authority before entering client information;</li>
              <li>comply with applicable laws and professional obligations;</li>
              <li>keep login credentials secure;</li>
              <li>maintain secure devices;</li>
              <li>review AI-generated outputs professionally.</li>
            </ul>
            <p>Users must not:</p>
            <ul className="list-disc pl-6">
              <li>upload unlawful content;</li>
              <li>upload information they are not authorised to process or share;</li>
              <li>attempt unauthorised access;</li>
              <li>reverse engineer or disrupt the platform;</li>
              <li>share credentials improperly;</li>
              <li>submit PHI or identifiable client information into PlayFirst Lite.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">7. Consent and Client Information</h2>
            <p>Users are responsible for:</p>
            <ul className="list-disc pl-6">
              <li>obtaining and documenting appropriate consent or authority;</li>
              <li>determining what client information is entered into PlayFirst;</li>
              <li>complying with professional and legal obligations.</li>
            </ul>
            <p>Users acknowledge that PlayFirst does not independently verify client consent.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">8. AI-Generated Content</h2>
            <p>Users acknowledge that:</p>
            <ul className="list-disc pl-6">
              <li>AI outputs may contain inaccuracies or omissions;</li>
              <li>outputs require professional review;</li>
              <li>outputs may not always be clinically appropriate;</li>
              <li>PlayFirst makes no guarantee regarding accuracy or suitability.</li>
            </ul>
            <p>AI-generated content must be treated as a draft.</p>
            <p>Users remain responsible for all final documents and professional decisions.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">9. Accounts and Security</h2>
            <p>Users are responsible for:</p>
            <ul className="list-disc pl-6">
              <li>maintaining password confidentiality;</li>
              <li>securing their devices;</li>
              <li>preventing unauthorised access;</li>
              <li>notifying PlayFirst of suspicious activity.</li>
            </ul>
            <p>PlayFirst may suspend accounts where necessary for:</p>
            <ul className="list-disc pl-6">
              <li>security;</li>
              <li>abuse prevention;</li>
              <li>legal compliance;</li>
              <li>operational integrity.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">10. Subscription and Payments</h2>
            <p>Some PlayFirst features require paid subscriptions.</p>
            <p>Payments are processed by third-party providers.</p>
            <p>Users remain responsible for:</p>
            <ul className="list-disc pl-6">
              <li>payment obligations;</li>
              <li>subscription management;</li>
              <li>cancellation requests.</li>
            </ul>
            <p>PlayFirst may change pricing or features from time to time.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">11. Data Storage and Privacy</h2>
            <p>PlayFirst takes reasonable measures to protect information using industry-standard practices.</p>
            <p>Connections are protected using HTTPS/TLS.</p>
            <p>However, no online system can be guaranteed completely secure.</p>
            <p>Deleted data may remain temporarily in secure backups until routine backup expiry.</p>
            <p>Use of the Service is also governed by the PlayFirst Privacy Policy.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">12. International Processing</h2>
            <p>Users acknowledge that PlayFirst may use service providers located outside their country, including in the United States.</p>
            <p>Users remain responsible for ensuring their use complies with applicable local obligations.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">13. Intellectual Property</h2>
            <p>PlayFirst retains rights relating to:</p>
            <ul className="list-disc pl-6">
              <li>software;</li>
              <li>branding;</li>
              <li>design;</li>
              <li>workflows;</li>
              <li>proprietary functionality.</li>
            </ul>
            <p>Users retain ownership of content they create or upload.</p>
            <p>Users grant PlayFirst a limited licence necessary to operate the service.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">14. Acceptable Use</h2>
            <p>Users must not:</p>
            <ul className="list-disc pl-6">
              <li>use PlayFirst unlawfully;</li>
              <li>upload harmful code or malicious content;</li>
              <li>attempt unauthorised access;</li>
              <li>disrupt the service;</li>
              <li>misuse parent portal functionality;</li>
              <li>use the service for fraudulent purposes;</li>
              <li>submit PHI or identifiable client information into PlayFirst Lite.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">15. Suspension or Termination</h2>
            <p>PlayFirst may suspend or terminate access where necessary for:</p>
            <ul className="list-disc pl-6">
              <li>security;</li>
              <li>legal compliance;</li>
              <li>abuse prevention;</li>
              <li>operational integrity;</li>
              <li>violation of these Terms.</li>
            </ul>
            <p>Users may request account deletion.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">16. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, PlayFirst shall not be liable for:</p>
            <ul className="list-disc pl-6">
              <li>clinical outcomes;</li>
              <li>decisions made using generated content;</li>
              <li>indirect or consequential damages;</li>
              <li>business interruption;</li>
              <li>data loss except where liability cannot legally be excluded.</li>
            </ul>
            <p>Use of the platform is at the user's own professional discretion.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">17. Indemnification</h2>
            <p>Users agree to indemnify and hold PlayFirst harmless from claims arising from:</p>
            <ul className="list-disc pl-6">
              <li>clinical use of the platform;</li>
              <li>misuse of client information;</li>
              <li>violation of these Terms;</li>
              <li>failure to obtain appropriate consent or authority;</li>
              <li>unlawful or unauthorised data processing.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">18. Governing Law</h2>
            <p>These Terms are governed by the laws of South Africa.</p>
            <p>Nothing in these Terms limits rights that cannot legally be excluded under applicable local law.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">19. Changes to the Terms</h2>
            <p>PlayFirst may update these Terms from time to time.</p>
            <p>Updated versions will be posted on the website or platform.</p>
            <p>Continued use of the platform after updates are published may constitute acceptance of the updated Terms.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">20. Contact Information</h2>
            <p>
              For legal or privacy-related matters contact:{' '}
              <a href="mailto:michaela@playfirstapp.com" className="text-primary underline">michaela@playfirstapp.com</a>
            </p>
            <p>
              Website:{' '}
              <a href="https://www.playfirstapp.com" className="text-primary underline" target="_blank" rel="noopener noreferrer">https://www.playfirstapp.com</a>
            </p>

          </article>
        </motion.div>
      </div>
    </div>
  );
}
